import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api } from '@/services/api';
import { resetPasswordSchema, formatZodErrors } from '@/shared/validation';
import { FormError } from '@/shared/FormError';

/**
 * Imposta la nuova password dal link ricevuto via email
 * (/reset-password?token=...). Il token e' monouso e scade in 30 minuti:
 * su errore l'utente viene rimandato a richiedere un nuovo link.
 */
export function ResetPasswordPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    const parsed = resetPasswordSchema.safeParse({ password, confirm });
    if (!parsed.success) {
      setFieldErrors(formatZodErrors(parsed));
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword: password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Reset non riuscito. Il link potrebbe essere scaduto.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-shell">
      <div className="login-container">
        <div className="login-logo">
          <img src="/icon-128.webp" alt="VVF"
            style={{ width: 72, height: 72, margin: '0 auto 10px', display: 'block' }} />
          <h1>VVF Tracker</h1>
          <p>Nuova password</p>
        </div>

        <div className="login-form-wrap">
          <h2>Reimposta password</h2>

          {!token ? (
            <>
              <div className="error-message">
                Link non valido: manca il token di reset. Apri il link
                ricevuto via email oppure richiedi un nuovo reset.
              </div>
              <div className="form-footer">
                <Link to="/forgot-password">Richiedi nuovo link</Link>
              </div>
            </>
          ) : done ? (
            <div style={{ background: '#d4edda', color: '#155724', padding: 14, borderRadius: 8, fontSize: 14 }}>
              ✅ Password aggiornata! Tra un istante verrai portato al login…
            </div>
          ) : (
            <>
              {error && (
                <div className="error-message">
                  {error}{' '}
                  <Link to="/forgot-password" style={{ fontWeight: 600 }}>Richiedi nuovo link</Link>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="form-group">
                  <label htmlFor="password">Nuova password</label>
                  <input id="password" type="password" value={password}
                    onChange={(e) => setPassword(e.target.value)} autoFocus />
                  <FormError message={fieldErrors.password} />
                </div>

                <div className="form-group">
                  <label htmlFor="confirm">Conferma password</label>
                  <input id="confirm" type="password" value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} />
                  <FormError message={fieldErrors.confirm} />
                </div>

                <button type="submit" className="submit-btn" disabled={loading}>
                  {loading ? 'Salvataggio...' : 'Imposta nuova password'}
                </button>
              </form>

              <div className="form-footer">
                <Link to="/login">← Torna al login</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
